import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

const PublicLayout = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMounted(true);
    document.body.classList.add('public-page');
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      document.body.classList.remove('public-page');
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/features', label: 'Features' },
    { path: '/docs', label: 'Docs' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen w-full flex flex-col public-layout-bg">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Aurora gradient layers */}
        <div className="absolute inset-0 aurora-layer" />
        
        {/* Large floating orbs */}
        <div className="absolute rounded-full orb-1" />
        <div className="absolute rounded-full orb-2" />
        <div className="absolute rounded-full orb-3" />
        <div className="absolute rounded-full orb-4" />


        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute rounded-full particle particle-${(i % 4) + 1}`}
            style={{
              width: `${2 + (i % 4) * 2}px`,
              height: `${2 + (i % 4) * 2}px`,
              left: `${5 + (i * 4.5) % 90}%`,
              top: `${10 + (i * 7) % 80}%`,
              animationDelay: `${i * 0.3}s`,
              '--particle-color': i % 3 === 0 
                ? 'rgba(50, 205, 50, 0.8)' 
                : i % 3 === 1 
                  ? 'rgba(34, 139, 34, 0.6)' 
                  : 'rgba(152, 251, 152, 0.5)',
              '--particle-glow': `0 0 ${6 + (i % 3) * 4}px rgba(50, 205, 50, ${0.3 + (i % 3) * 0.1})`,
              '--animation-duration': `${10 + (i % 5) * 3}s`,
            }}
          />
        ))}

        {/* Glowing lines */}
        <svg className="absolute inset-0 w-full h-full opacity-40">
          <defs>
            <linearGradient id="lineGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(50, 205, 50, 0)" />
              <stop offset="50%" stopColor="rgba(50, 205, 50, 0.3)" />
              <stop offset="100%" stopColor="rgba(50, 205, 50, 0)" />
            </linearGradient>
            <linearGradient id="lineGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(34, 139, 34, 0)" />
              <stop offset="50%" stopColor="rgba(34, 139, 34, 0.25)" />
              <stop offset="100%" stopColor="rgba(34, 139, 34, 0)" />
            </linearGradient>
          </defs>
          <line x1="0%" y1="30%" x2="100%" y2="70%" stroke="url(#lineGradient1)" strokeWidth="1" className="line-pulse" />
          <line x1="100%" y1="20%" x2="0%" y2="80%" stroke="url(#lineGradient2)" strokeWidth="1" className="line-pulse-reverse" />
          <line x1="20%" y1="0%" x2="80%" y2="100%" stroke="url(#lineGradient1)" strokeWidth="0.5" className="line-pulse-delayed" />
        </svg>

        {/* Floating glassmorphic shapes */}
        <div className="absolute w-40 h-40 rounded-3xl glass-shape-1" />
        <div className="absolute w-32 h-32 rounded-2xl glass-shape-2" />
        <div className="absolute w-24 h-24 rounded-xl glass-shape-3" />
        <div className="absolute w-28 h-28 rounded-2xl glass-shape-4" />

        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015] noise-texture" />
      </div>

      {/* Header */}
      <header 
        className={`sticky top-0 z-50 px-6 lg:px-12 py-4 transition-all duration-600 ease-out ${
          scrolled ? 'header-scrolled' : 'header-transparent'
        }`}
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(-20px)',
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/logo.png"
              alt="ForgeVault Logo" 
              className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="text-xl font-bold text-text-primary transition-colors duration-300 group-hover:text-primary-400">ForgeVault</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-300 relative ${
                  isActive(link.path) ? 'text-primary-500' : 'text-primary-300/70 hover:text-primary-500'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-primary-500" />
                )}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 text-primary-300/80 hover:text-primary-500 hover:-translate-y-0.5"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 relative overflow-hidden logo-gradient text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
            >
              <span className="absolute inset-0 shimmer-effect" />
              <span className="relative z-10">Get Started</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg transition-colors duration-200 text-primary-500"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 rounded-xl mobile-menu-bg backdrop-blur-xl border border-primary-500/15">
            <nav className="flex flex-col gap-2 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path) 
                      ? 'text-primary-500 bg-primary-500/10' 
                      : 'text-primary-300/70'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t my-2 border-primary-500/10" />
              <Link
                to="/login"
                className="py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 text-primary-300/70"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="py-2.5 px-3 rounded-lg text-sm font-semibold text-center logo-gradient text-black"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer 
        className="relative z-10 px-6 lg:px-12 py-8 border-t border-primary-500/8 transition-opacity duration-800"
        style={{
          opacity: mounted ? 1 : 0,
          transitionDelay: '0.5s',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo & Copyright */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-sm text-primary-300/50">
                © 2025 ForgeVault. All rights reserved.
              </span>
            </div>

            {/* Footer Links */}
            <nav className="flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm text-primary-300/50 hover:text-primary-500 transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/login"
                className="text-sm text-primary-300/50 hover:text-primary-500 transition-colors duration-200"
              >
                Sign In
              </Link>
            </nav>
          </div>
        </div>
      </footer>

      <style>{`
        /* Background - dark with subtle green tint */
        .public-layout-bg {
          background: #040906;
        }
        
        /* Hide scrollbar but allow scrolling */
        .public-page::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
        .public-page {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        
        /* Header states */
        .header-scrolled {
          background: rgba(4, 9, 6, 0.9);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(50, 205, 50, 0.12);
        }
        .header-transparent {
          background: transparent;
          border-bottom: 1px solid transparent;
        }
        
        /* Logo gradient */
        .logo-gradient {
          background: linear-gradient(135deg, #228B22 0%, #32CD32 100%);
        }
        
        /* Mobile menu background */
        .mobile-menu-bg {
          background: rgba(8, 14, 10, 0.95);
        }
        
        /* Aurora layer */
        .aurora-layer {
          background:
            radial-gradient(ellipse 120% 80% at 50% 0%, rgba(50, 205, 50, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 80% 60% at 80% 50%, rgba(34, 139, 34, 0.12) 0%, transparent 45%),
            radial-gradient(ellipse 70% 50% at 20% 80%, rgba(50, 205, 50, 0.1) 0%, transparent 45%),
            radial-gradient(ellipse 60% 40% at 60% 100%, rgba(34, 139, 34, 0.08) 0%, transparent 40%);
          animation: auroraShift 15s ease-in-out infinite alternate;
        }
        
        /* Floating orbs */
        .orb-1 {
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.18) 0%, rgba(50, 205, 50, 0.05) 40%, transparent 70%);
          top: -20%;
          left: 10%;
          filter: blur(60px);
          animation: floatOrb1 30s ease-in-out infinite;
        }
        .orb-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(34, 139, 34, 0.15) 0%, rgba(34, 139, 34, 0.03) 45%, transparent 70%);
          bottom: -10%;
          right: -5%;
          filter: blur(50px);
          animation: floatOrb2 35s ease-in-out infinite;
        }
        .orb-3 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.12) 0%, transparent 60%);
          top: 40%;
          left: -10%;
          filter: blur(40px);
          animation: floatOrb3 25s ease-in-out infinite;
        }
        .orb-4 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.1) 0%, transparent 60%);
          top: 60%;
          right: 20%;
          filter: blur(45px);
          animation: floatOrb4 28s ease-in-out infinite;
        }
        
        
        /* Particles */
        .particle {
          background: var(--particle-color);
          box-shadow: var(--particle-glow);
        }
        .particle-1 { animation: floatParticle1 var(--animation-duration) ease-in-out infinite; }
        .particle-2 { animation: floatParticle2 var(--animation-duration) ease-in-out infinite; }
        .particle-3 { animation: floatParticle3 var(--animation-duration) ease-in-out infinite; }
        .particle-4 { animation: floatParticle4 var(--animation-duration) ease-in-out infinite; }
        
        /* Line pulse animations */
        .line-pulse { animation: linePulse 8s ease-in-out infinite; }
        .line-pulse-reverse { animation: linePulse 10s ease-in-out infinite reverse; }
        .line-pulse-delayed { animation: linePulse 12s ease-in-out infinite; animation-delay: 2s; }
        
        /* Glass shapes */
        .glass-shape-1 {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.15) 0%, rgba(34, 139, 34, 0.05) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(50, 205, 50, 0.12);
          top: 8%;
          left: 5%;
          transform: rotate(12deg);
          animation: floatShape1 18s ease-in-out infinite;
        }
        .glass-shape-2 {
          background: linear-gradient(135deg, rgba(34, 139, 34, 0.12) 0%, rgba(50, 205, 50, 0.04) 100%);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(34, 139, 34, 0.1);
          top: 15%;
          right: 8%;
          transform: rotate(-15deg);
          animation: floatShape2 22s ease-in-out infinite;
        }
        .glass-shape-3 {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.1) 0%, transparent 100%);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(50, 205, 50, 0.08);
          bottom: 20%;
          left: 10%;
          transform: rotate(-8deg);
          animation: floatShape3 16s ease-in-out infinite;
        }
        .glass-shape-4 {
          background: linear-gradient(135deg, rgba(34, 139, 34, 0.1) 0%, rgba(50, 205, 50, 0.03) 100%);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(34, 139, 34, 0.08);
          bottom: 12%;
          right: 12%;
          transform: rotate(20deg);
          animation: floatShape4 20s ease-in-out infinite;
        }
        
        /* Noise texture */
        .noise-texture {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        }
        
        /* Shimmer effect */
        .shimmer-effect {
          background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%);
          animation: shimmer 2s infinite;
        }
        
        /* Keyframe animations */
        @keyframes auroraShift {
          0% { opacity: 1; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.8; transform: scale(1.05) rotate(1deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(80px, 40px) scale(1.1); }
          50% { transform: translate(40px, 80px) scale(0.95); }
          75% { transform: translate(-40px, 40px) scale(1.05); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-60px, -50px) scale(1.15); }
          50% { transform: translate(-100px, 20px) scale(0.9); }
          75% { transform: translate(-30px, -30px) scale(1.05); }
        }
        @keyframes floatOrb3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(60px, -40px) scale(1.1); }
          66% { transform: translate(30px, 50px) scale(0.95); }
        }
        @keyframes floatOrb4 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-50px, -60px) scale(1.08); }
        }
        @keyframes floatParticle1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          25% { transform: translate(15px, -25px) scale(1.2); opacity: 1; }
          50% { transform: translate(30px, -10px) scale(0.9); opacity: 0.7; }
          75% { transform: translate(10px, 15px) scale(1.1); opacity: 0.9; }
        }
        @keyframes floatParticle2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          33% { transform: translate(-20px, -30px) scale(1.3); opacity: 1; }
          66% { transform: translate(25px, -15px) scale(0.85); opacity: 0.6; }
        }
        @keyframes floatParticle3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.7; }
          50% { transform: translate(-15px, 25px) scale(1.15); opacity: 1; }
        }
        @keyframes floatParticle4 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          25% { transform: translate(20px, 20px) scale(1.25); opacity: 0.9; }
          75% { transform: translate(-25px, -20px) scale(0.9); opacity: 0.5; }
        }
        @keyframes linePulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.6; }
        }
        @keyframes floatShape1 {
          0%, 100% { transform: translate(0, 0) rotate(12deg); }
          25% { transform: translate(20px, -15px) rotate(18deg); }
          50% { transform: translate(35px, 10px) rotate(15deg); }
          75% { transform: translate(10px, 25px) rotate(8deg); }
        }
        @keyframes floatShape2 {
          0%, 100% { transform: translate(0, 0) rotate(-15deg); }
          33% { transform: translate(-25px, 20px) rotate(-10deg); }
          66% { transform: translate(-15px, -15px) rotate(-20deg); }
        }
        @keyframes floatShape3 {
          0%, 100% { transform: translate(0, 0) rotate(-8deg); }
          50% { transform: translate(30px, -20px) rotate(-2deg); }
        }
        @keyframes floatShape4 {
          0%, 100% { transform: translate(0, 0) rotate(20deg); }
          25% { transform: translate(-20px, -25px) rotate(25deg); }
          75% { transform: translate(15px, -10px) rotate(15deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

PublicLayout.propTypes = {
  children: PropTypes.node,
};

export default PublicLayout;
